import type {
  ConnexionContext,
  LiveBlocker,
  LivePhase,
  LiveQuality,
  LiveState,
  LiveView,
  LiveViewInput,
  LiveWaitingOn,
  PhaseState,
} from "./liveView.types";
import type { GetLiveStatsResponse } from "../api/EncoderAPI.types";
import type { LiveStreamStatus } from "../models/LiveStream";

export type {
  LivePendingAction,
  LivePhase,
  LiveState,
  LiveView,
  LiveViewInput,
  LiveWaitingOn,
} from "./liveView.types";

export const LIVE_ACTIVATION_TIMEOUT_MS = 300_000;
// Product rule: a scheduled live is « disponible 5 minutes avant l'heure de
// début », the backend activates it at start - 5 min.
export const LIVE_SCHEDULED_ACTIVATION_LEAD_MS = 300_000;
export const LIVE_FINALIZE_DELAY_MS = 10_000;

const TERMINAL_STATUSES: LiveStreamStatus[] = ["stopped", "finished", "clean"];

export function isTerminalStatus(status: LiveStreamStatus): boolean {
  return TERMINAL_STATUSES.includes(status);
}
const IN_PROGRESS_STATUSES: LiveStreamStatus[] = [
  "activating",
  "ongoing",
  "live",
];

const CONNECTED = "CONNECTED";
const DISCONNECTED = "DISCONNECTED";

const PHASE_BY_STATUS: Record<LiveStreamStatus, LivePhase> = {
  none: "preparation",
  scheduled: "preparation",
  activating: "connexion",
  ongoing: "connexion",
  live: "antenne",
  stopped: "termine",
  finished: "termine",
  clean: "termine",
};

const WAITING_ON: Record<LiveState, LiveWaitingOn> = {
  blocked: "user",
  ready: "user",
  scheduled: "user",
  provisioning: "okast",
  provisioningFailed: "user",
  waitingSignal: "obs",
  checkingSignal: "okast",
  readyToAir: "user",
  publishing: "okast",
  live: null,
  degraded: null,
  signalLost: "obs",
  stopping: "okast",
  replayAvailable: "user",
  replayCreated: null,
  replayUnavailable: null,
  ended: null,
};

function isKnownStatus(status: string): status is LiveStreamStatus {
  return status in PHASE_BY_STATUS;
}

function toQuality(value: string | null | undefined): LiveQuality | null {
  if (value === "healthy" || value === "degraded") return value;
  if (value === "outage" || value === "no_data") return value;
  return null;
}

// Backend dates are UTC strings, sometimes without a timezone designator.
export function parseUtcMs(value: string | null | undefined): number | null {
  if (!value) return null;
  const hasZone = /(Z|[+-]\d{2}:?\d{2})$/.test(value);
  const ms = Date.parse(hasZone ? value : `${value}Z`);
  return Number.isNaN(ms) ? null : ms;
}

function nonEmpty(value: string | null | undefined): string | null {
  return value ? String(value) : null;
}

export function getServerStatus(
  input: Pick<
    LiveViewInput,
    "media" | "mediaUpdatedAt" | "stats" | "statsUpdatedAt"
  >,
): LiveStreamStatus {
  const mediaStatus = input.media.live_stream?.status ?? "none";
  const statsStatus = input.stats?.status;

  // A terminal status seen on the stats stays authoritative while the media
  // still reports an in-progress live: it simply has not caught up yet, and
  // trusting it would bounce the view back to "on air" on every media refetch.
  if (
    statsStatus &&
    isTerminalStatus(statsStatus) &&
    IN_PROGRESS_STATUSES.includes(mediaStatus)
  ) {
    return statsStatus;
  }
  if (statsStatus && input.statsUpdatedAt > input.mediaUpdatedAt) {
    return statsStatus;
  }
  return mediaStatus;
}

function getBlockers(input: LiveViewInput): LiveBlocker[] {
  const blockers: LiveBlocker[] = [];
  if (!input.isSaved) blockers.push("unsaved");
  if (!input.isQuotaAvailable) blockers.push("quota");
  if (!input.hasValidSettings) blockers.push("settings");
  return blockers;
}

function getTerminalState(
  input: LiveViewInput,
  status: LiveStreamStatus,
): LiveState {
  const { pending, terminalSeenAt, now } = input;

  if (pending?.action === "stop" && terminalSeenAt === null) return "stopping";
  if (
    terminalSeenAt !== null &&
    now - terminalSeenAt < LIVE_FINALIZE_DELAY_MS
  ) {
    return "stopping";
  }
  if (input.media.replay_uuid) return "replayCreated";
  if (!input.allowReplay) return "ended";
  if (status === "clean" || !input.media.can_replay) {
    return "replayUnavailable";
  }
  return "replayAvailable";
}

function getPendingPhaseState(
  input: LiveViewInput,
  status: LiveStreamStatus | null,
): PhaseState | null {
  const action = input.pending?.action;
  const isNotStarted =
    status === null || status === "none" || status === "scheduled";

  if (action === "create" && isNotStarted) {
    return { phase: "connexion", state: "provisioning" };
  }
  if (
    action === "publish" &&
    (status === "activating" || status === "ongoing")
  ) {
    return { phase: "connexion", state: "publishing" };
  }
  if (
    action === "stop" &&
    (status === null || PHASE_BY_STATUS[status] !== "termine")
  ) {
    return { phase: "termine", state: "stopping" };
  }
  return null;
}

function getPreparationState(
  input: LiveViewInput,
  status: LiveStreamStatus | null,
): LiveState {
  if (getBlockers(input).length > 0) return "blocked";
  return status === "scheduled" ? "scheduled" : "ready";
}

function getActivationStartMs(
  input: LiveViewInput,
  status: LiveStreamStatus | null,
  liveStartRaw: string | null,
): number | null {
  const { pending, now } = input;
  // A retry (pending create) restarts the window even if live_start is old.
  const retryStartMs = pending?.action === "create" ? pending.since : null;
  const parsedStartMs =
    status === "activating" && liveStartRaw ? parseUtcMs(liveStartRaw) : null;
  if (parsedStartMs === null) return retryStartMs;

  // A scheduled live carries its future start: the activation began earlier.
  const isScheduledStart =
    parsedStartMs > now || input.scheduledStartSeen === liveStartRaw;
  const serverStartMs = isScheduledStart
    ? parsedStartMs - LIVE_SCHEDULED_ACTIVATION_LEAD_MS
    : parsedStartMs;
  if (retryStartMs === null) return serverStartMs;
  return Math.max(serverStartMs, retryStartMs);
}

function getConnexionState(
  input: LiveViewInput,
  ctx: ConnexionContext,
): LiveState {
  const { status, liveStatus, quality, hasCredentials, liveStartRaw } = ctx;
  const startMs = getActivationStartMs(input, status, liveStartRaw);
  const timedOut =
    startMs !== null && input.now - startMs > LIVE_ACTIVATION_TIMEOUT_MS;
  const signalKnownNotConnected =
    liveStatus !== undefined && liveStatus !== CONNECTED;

  if (
    !hasCredentials &&
    liveStatus !== CONNECTED &&
    (input.detailsError || timedOut)
  ) {
    return "provisioningFailed";
  }
  if (
    (hasCredentials || status === "ongoing") &&
    liveStatus !== CONNECTED &&
    (hasCredentials || signalKnownNotConnected)
  ) {
    return "waitingSignal";
  }
  if (!hasCredentials && liveStatus !== CONNECTED) return "provisioning";
  if (status === "ongoing" && quality !== "no_data") return "readyToAir";
  if (quality === "no_data" || status === "activating") return "checkingSignal";
  return "readyToAir";
}

function getAntenneState(
  stats: GetLiveStatsResponse | null,
  quality: LiveQuality | null,
): LiveState {
  if (stats && stats.live_status !== CONNECTED) return "signalLost";
  if (quality === "degraded" || quality === "outage") return "degraded";
  return "live";
}

function getPhaseState(
  phase: LivePhase,
  input: LiveViewInput,
  status: LiveStreamStatus | null,
  connexionCtx: ConnexionContext,
): LiveState {
  switch (phase) {
    case "preparation":
      return getPreparationState(input, status);
    case "connexion":
      return getConnexionState(input, connexionCtx);
    case "antenne":
      return getAntenneState(input.stats, connexionCtx.quality);
    default:
      return status ? getTerminalState(input, status) : "replayUnavailable";
  }
}

function getLiveEnd(
  phase: LivePhase,
  stats: GetLiveStatsResponse | null,
  liveStream: LiveViewInput["media"]["live_stream"],
): string | null {
  if (phase === "antenne") {
    return nonEmpty(stats?.live_end ? String(stats.live_end) : null);
  }
  if (phase === "termine") return nonEmpty(liveStream?.live_end);
  return null;
}

export function getLiveView(input: LiveViewInput): LiveView {
  const { media, stats, details } = input;
  const liveStream = media.live_stream;
  const manifest = liveStream?.manifest;

  const rawStatus = getServerStatus(input);
  const status: LiveStreamStatus | null = isKnownStatus(rawStatus)
    ? rawStatus
    : null;

  const streamUrl =
    nonEmpty(details?.stream_url) ?? nonEmpty(manifest?.stream_url);
  const streamKey =
    nonEmpty(details?.stream_key) ?? nonEmpty(manifest?.stream_key);

  const liveStatus = stats?.live_status;
  const quality = toQuality(stats?.status_quality);
  const liveStartRaw =
    nonEmpty(liveStream?.live_start) ??
    (stats?.live_start ? String(stats.live_start) : null);

  const connexionCtx: ConnexionContext = {
    status,
    liveStatus,
    quality,
    hasCredentials: streamUrl !== null && streamKey !== null,
    liveStartRaw,
  };

  // Rule 1: a local action not yet reflected by the server wins.
  // Rule 2: phase from the freshest server status (unknown -> preparation).
  // Rule 3: sub-state within the phase, top to bottom.
  const basePhase: LivePhase = status ? PHASE_BY_STATUS[status] : "preparation";
  const { phase, state } = getPendingPhaseState(input, status) ?? {
    phase: basePhase,
    state: getPhaseState(basePhase, input, status, connexionCtx),
  };

  const replayStart = manifest?.replay_start_ms;
  const replayEnd = manifest?.replay_end_ms;
  const replayRangeMs =
    state === "replayCreated" &&
    typeof replayStart === "number" &&
    typeof replayEnd === "number"
      ? { start: replayStart, end: replayEnd }
      : null;

  return {
    phase,
    state,
    waitingOn: WAITING_ON[state],
    blockers: state === "blocked" ? getBlockers(input) : [],
    obsDisconnected: state === "waitingSignal" && liveStatus === DISCONNECTED,
    quality: phase === "connexion" || phase === "antenne" ? quality : null,
    streamUrl,
    streamKey,
    scheduledStart:
      phase === "preparation" && status === "scheduled" ? liveStartRaw : null,
    openedAt:
      phase === "connexion" || phase === "antenne" || phase === "termine"
        ? liveStartRaw
        : null,
    liveEnd: getLiveEnd(phase, stats, liveStream),
    stopReason: nonEmpty(manifest?.stop_reason),
    replayUuid: nonEmpty(media.replay_uuid),
    replayRangeMs,
  };
}
